/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the Data Grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the Data Grid
import "./EntityVerifications.scss";
import {TableFooter} from "../../common/components/TableWithPaging";

import React, {
    useEffect,
    useState,
} from "react";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector } from "react-redux";
import {Button} from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import {
    find,
    sortBy,
} from 'lodash';

import FormTextInput from "../../common/components/FormTextInput";
import config from "../../config";
import * as Api from "../../common/Api";

import {
    clearSelected,
    setSearchCriteria,
    getEntityVerificationsCount,
    getDenormCompanies,
    getDenormByCompanyId,
    dismissMessage,
    updateRowData,
    updateIndividualRow,
    updateFromServer,
} from "./entityVerificationsSlice";

import BreadCrumb from "../../common/components/BreadCrumb";
import DropdownAction from "../../common/components/DropdownAction";
import PanelHeader from "../../common/components/layout/PanelHeader";
import SpinnerOverlay from "../../common/components/SpinnerOverlay";

import Container from "../../common/components/layout/Container";
import {AgGridReact} from "ag-grid-react";
import {AttributeEditor} from "./AttributeEditor";
import {AttributeRenderer} from "./AttributeRenderer";
import {AddressAttributeEditor} from "./AddressAttributeEditor";
import {AddressAttributeRenderer} from "./AddressAttributeRenderer";
import {ContactRenderer} from "./ContactRenderer";
import {ContactEditor} from "./ContactEditor";

// Global flag to track if any cell editor is open
window.isCellEditorOpen = false;

const EntityVerifications = (props) => {
    return (
        <EntityVerificationComponent />
    );
};

function TablePaginator() {
    return null;
}

const EntityVerificationComponent = (props) => {
    const breadcrumbItems = [{label: 'Home', href: '/admin/home', isActive: false}, {
        label: 'Match Types',
        href: '',
        isActive: true
    }];
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pageSize = 15; //TODO- Make this a configurable parameter?
    const [pageIndex, setPageIndex] = useState(0); // 0-based index for API calls
    const [lastReload, setLastReload] = useState(Date.now());
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [searchCriteria, setSearchCriteria] = useState();
    const [showUpdatingMessage, setShowUpdatingMessage] = useState(true);
    const [gridApi, setGridApi] = useState(null);

    // 🔹 Track rows that need a visual refresh after Redux updates
    const [rowsNeedingRefresh, setRowsNeedingRefresh] = useState([]);

    //Get Redux Values
    const entityVerificationsState = useSelector(state => state.entityVerifications);
    const isReload = entityVerificationsState.reload === true;

    useEffect(() => {
        dispatch(getEntityVerificationsCount());
        const pageSelector = {pageNumber: pageIndex, pageSize: pageSize};
        dispatch(getDenormCompanies(pageSelector));
    }, [isReload, dispatch]);

    // Add grid ready handler
    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    // 🔹 Modify the refresh mechanism: poll → dispatch updates → mark rows for refresh
    useEffect(() => {
        const hasUpdatingItems = entityVerificationsState.entityVerificationsArray.some(
            ev => ev._updating && ev._updating.length > 0
        );

        console.log('Polling check:', {
            hasUpdatingItems,
            isCellEditorOpen: window.isCellEditorOpen,
            arrayLength: entityVerificationsState.entityVerificationsArray.length,
            updatingItems: entityVerificationsState.entityVerificationsArray.filter(ev => ev._updating && ev._updating.length > 0)
        });

        let refreshInterval = null;

        // Start polling if there are updating items
        if (hasUpdatingItems && gridApi) {
            console.log('Starting polling');
            refreshInterval = setInterval(async () => {
                // Check if any cell editor is open before proceeding with polling
                if (window.isCellEditorOpen) {
                    console.log('Polling skipped - cell editor is open');
                    return;
                }

                try {
                    console.log('Polling for updates...');
                    // Use Api.get() for proper authentication
                    const newData = await Api.get(true,
                        `${config.services.ATLAS.apiUrl}/managed-entity-verifications?pageNumber=${pageIndex}&pageSize=${pageSize}`
                    );

                    console.log('Received polling data:', {
                        newDataLength: newData.length,
                        newData: newData.slice(0, 2) // Log first 2 items
                    });

                    // Only update rows that have different _updating arrays
                    let hasChanges = false;
                    const changedRowIds = [];

                    newData.forEach((serverRow) => {
                        // Find the corresponding row in our array by ID
                        const existingRowIndex = entityVerificationsState.entityVerificationsArray.findIndex(row => row.id === serverRow.id);

                        if (existingRowIndex >= 0) {
                            const existingRow = entityVerificationsState.entityVerificationsArray[existingRowIndex];
                            const serverUpdating = serverRow._updating || [];
                            const existingUpdating = existingRow._updating || [];

                            console.log('Comparing rows:', {
                                rowId: serverRow.id,
                                serverUpdating,
                                existingUpdating,
                                areDifferent: JSON.stringify(serverUpdating) !== JSON.stringify(existingUpdating)
                            });

                            // Only update if the _updating arrays are different
                            if (JSON.stringify(serverUpdating) !== JSON.stringify(existingUpdating)) {
                                // Update only the specific row
                                const updatedRow = {
                                    ...existingRow,
                                    ...serverRow,
                                    _updating: serverUpdating
                                };

                                // Only replace the row if it's actually different
                                if (JSON.stringify(updatedRow) !== JSON.stringify(existingRow)) {
                                    dispatch(updateIndividualRow({
                                        rowId: serverRow.id,
                                        updatedRow: updatedRow
                                    }));
                                    hasChanges = true;
                                    changedRowIds.push(serverRow.id);
                                    console.log('Updated row:', serverRow.id);
                                }
                            }
                        }
                    });

                    if (hasChanges) {
                        console.log('Rows changed, scheduling refresh:', changedRowIds);

                        // 🔹 Defer the actual grid refresh to a separate effect
                        setRowsNeedingRefresh(prev => {
                            const setPrev = new Set(prev);
                            changedRowIds.forEach(id => setPrev.add(id));
                            return Array.from(setPrev);
                        });
                    } else {
                        console.log('No changes detected');
                    }
                } catch (error) {
                    console.error('Polling error:', error);
                }
            }, 5000);
        }

        // Cleanup function that will be called when the effect is cleaned up
        return () => {
            if (refreshInterval) {
                console.log('Clearing polling interval');
                clearInterval(refreshInterval);
            }
        };
    }, [
        entityVerificationsState.entityVerificationsArray,
        pageIndex,
        pageSize,
        gridApi,
        dispatch
    ]);

    // 🔹 After Redux + React update, actually refresh the changed rows in the grid
    useEffect(() => {
        if (!gridApi || rowsNeedingRefresh.length === 0) return;

        const rowNodes = rowsNeedingRefresh
            .map(id => gridApi.getRowNode(id))
            .filter(Boolean);

        if (rowNodes.length > 0) {
            console.log('Refreshing cells for rows:', rowsNeedingRefresh);

            gridApi.refreshCells({
                rowNodes,
                // Refresh all columns for these rows; specify `columns` if you want to limit it
                force: true,
            });
        }

        // Clear after use
        setRowsNeedingRefresh([]);
    }, [rowsNeedingRefresh, gridApi]);

    useEffect(() => {
        // When open dialog, we clear state.
        dispatch(clearSelected());
    }, [lastReload, dispatch]);

    useEffect(() => {
        // When open dialog, we clear state.
        if (entityVerificationsState.alertMessage) {
            setAlertMessage(entityVerificationsState.alertMessage);
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }
    }, [entityVerificationsState.alertMessage]);

    const handlePageSubmit = (event) => {
        // event is 1-based from TableFooter, convert to 0-based index
        const newPageIndex = event - 1;
        try {
            const pageSelector = {pageNumber: newPageIndex, pageSize: pageSize};
            setPageIndex(newPageIndex);
            dispatch(getDenormCompanies(pageSelector));
        }
        catch (err){
            console.log(`Had an exception and err = ${JSON.stringify(err)}`);
        }
    };

    const handlePageChange = (index) => {
        alert(`In EntityVerifications.handlePageChage and index = ${index}`);
    };

    const handleSearchOnKeyDown = (event) => {
        if (event.code === 'Enter') {
            dispatch(getDenormByCompanyId(searchCriteria));
        }
    };

    const handleRefresh = (target) => {
        setLastReload(Date.now());
    };

    const getActionItems = () => {
        const actionItems = [];

        actionItems.push(
            {
                label: 'EDIT',
                disabled: true,
                href: '',
                onClickHandler: () => navigate(`/admin/match-types/${entityVerificationsState.selectedId[0]}/edit`),
                isActive: entityVerificationsState.selectedMatchTypes && entityVerificationsState.selectedMatchTypes.length === 1
            }
        );

        return sortBy(actionItems, 'label');
    };

    // Row Data: The data to be displayed.
    const [retrievedRowData, setRowData] = useState(
        entityVerificationsState.entityVerifications
    );

    const isEditAble = (props, attributeDenormPrefix) => {
        let updatingData = null;
        if (props.data._updating && props.data._updating.length > 0) {
            updatingData = !!find(
                props.data._updating,
                _updatingData => _updatingData.attributeDenormPrefix === attributeDenormPrefix
            );
        }

        console.log('isEditAble check:', {
            attributeDenormPrefix,
            hasUpdating: !!props.data._updating,
            updatingLength: props.data._updating?.length,
            updatingData,
            result: !updatingData
        });

        if (updatingData) {
            return false;
        }
        return true;
    };

    const dismissMsg = (id) => {
        dispatch(dismissMessage(id));
    };

    // Column Definitions: Defines the columns to be displayed.
    const [colDefs, setColDefs] = useState([
        {
            headerName: "Company Name",
            field: "companyName",
            editable: (props) => isEditAble(props, 'companyName'),
            pinned: "left",
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'companyName'},
            cellEditorParams: {
                attributeType: 'CompanyName',
                entityIdColumn: 'companyId',
                attributeDenormPrefix: 'companyName'
            }
        },
        {
            headerName: "Company ID",
            field: "companyId",
            pinned: "left",
            width: "100px"
        },
        {
            headerName: "Company URL",
            field: "companyUrl",
            editable: (props) => isEditAble(props, 'companyUrl'),
            // enableCellChangeFlash: true,
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'companyUrl'},
            cellEditorParams: {
                attributeType: 'CompanyURL',
                entityIdColumn: 'companyId',
                attributeDenormPrefix: 'companyUrl'
            }
        },
        {
            headerName: "Company Address",
            cellEditor: AddressAttributeEditor,
            cellRenderer: AddressAttributeRenderer,
            cellEditorPopup: true,
            minWidth: 400,
            editable: (props) => isEditAble(props, 'companyAddress'),
            cellRendererParams: {attributeDenormPrefix: 'companyAddress'},
            cellEditorParams: {
                attributeType: 'CompanyAddress',
                entityIdColumn: 'companyId',
                attributeDenormPrefix: 'companyAddress'
            },
        },
        {
            headerName: "Company Phone Number",
            field: "companyPhone",
            editable: (props) => isEditAble(props, 'companyPhone'),
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'companyPhone'},
            cellEditorParams: {
                attributeType: 'CompanyPhone',
                entityIdColumn:'companyId',
                attributeDenormPrefix: 'companyPhone',
                verifiedByOptions: 'phone_verified_by',
                invalidatedReasonOptions: 'phone_invalidated_reasons'
            },
        },
        {
            headerName: "Company Email",
            field: "companyEmail",
            editable: (props) => isEditAble(props, 'companyEmail'),
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'companyEmail'},
            cellEditorParams: {
                attributeType: 'CompanyEmail',
                entityIdColumn: 'companyId',
                attributeDenormPrefix: 'companyEmail'
            },
        },
        {
            headerName: "Company Primary Trade",
            field: "companyClassPrimaryTrade",
            editable: (props) => isEditAble(props, 'companyClassPrimaryTrade'),
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'companyClassPrimaryTrade'},
            cellEditorParams: {
                attributeType: 'CompanyTradeClassification',
                entityIdColumn: 'companyId',
                attributeDenormPrefix: 'companyClassPrimaryTrade'
            }
        },
        {
            headerName: "Contact ID",
            field: "contactId"
        },
        {
            headerName: "Contact Name",
            editable: (props) => isEditAble(props, 'contactName'),
            cellEditorPopup: true,
            cellRenderer: ContactRenderer,
            cellEditor: ContactEditor,
            cellRendererParams: {attributeDenormPrefix: 'contactName'},
            cellEditorParams: {
                attributeType: 'ContactName',
                entityIdColumn: 'contactId',
                attributeDenormPrefix: 'contactName'
            }
        },
        {
            headerName: "Contact Title",
            field: "contactTitle",
            editable: (props) => isEditAble(props, 'contactTitle'),
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'contactTitle'},
            cellEditorParams: {
                attributeType: 'ContactTitle',
                entityIdColumn: 'contactId',
                attributeDenormPrefix: 'contactTitle'
            }
        },
        {
            headerName: "Contact Phone Number",
            field: "contactPhone",
            editable: (props) => isEditAble(props, 'contactPhone'),
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'contactPhone'},
            cellEditorParams: {
                attributeType: 'ContactPhone',
                entityIdColumn: 'contactId',
                attributeDenormPrefix: 'contactPhone',
                verifiedByOptions: 'phone_verified_by',
                invalidatedReasonOptions: 'phone_invalidated_reasons'
            }
        },
        {
            headerName: "Contact Email",
            field: "contactEmail",
            editable: (props) => isEditAble(props, 'contactEmail'),
            cellEditorPopup: true,
            cellRenderer: AttributeRenderer,
            cellEditor: AttributeEditor,
            cellRendererParams: {attributeDenormPrefix: 'contactEmail'},
            cellEditorParams: {
                attributeType: 'ContactEmail',
                entityIdColumn: 'contactId',
                attributeDenormPrefix: 'contactEmail'
            },
        }
    ]);

    const errorUpdateColumn = find(entityVerificationsState.entityVerificationsArray,
        ev => ev._updating && find(ev._updating, evUpdaing => evUpdaing.statusId === 'FAILED' && !evUpdaing.dismissMessage));
    const showUpdatingColumn = find(entityVerificationsState.entityVerificationsArray,
        ev => ev._updating && find(ev._updating, evUpdaing => evUpdaing.statusId === 'UPDATING' && !evUpdaing.dismissMessage));

    // Add cell style handler
    const getRowStyle = (params) => {
        if (params.data._updating && params.data._updating.length > 0) {
            return { opacity: 0.5 };
        }
        return null;
    };

    return (
        <Container>
            <BreadCrumb items={breadcrumbItems}></BreadCrumb>
            {entityVerificationsState.loading && !entityVerificationsState.pollingLoading && (
                <SpinnerOverlay />
            )}
            <Alert
                variant={"danger"}
                show={showAlert}
                onClose={() => setShowAlert(!showAlert)}
                dismissible
                transition={false}
            >
                {alertMessage}
            </Alert>
            {errorUpdateColumn &&
                <Alert
                    variant={"danger"}
                    show={true}
                    onClose={() => dismissMsg(find(errorUpdateColumn._updating, evUpdaing => evUpdaing.statusId === 'FAILED')?.id)}
                    dismissible
                >
                    {find(errorUpdateColumn._updating, evUpdaing => evUpdaing.statusId === 'FAILED')?.errorMessage}
                </Alert>
            }
            {showUpdatingMessage && showUpdatingColumn &&
                <Alert
                    variant={"warning"}
                    show={true}
                    onClose={() => setShowUpdatingMessage(false)}
                    dismissible
                >
                    We are updating record.
                </Alert>
            }

            <PanelHeader
                header={`Entity Verification Records (${entityVerificationsState.totalCount.toLocaleString()})`}
                toolAction={
                    <div>
                        <Button
                            className="btn-min-width-120 btn-height-35"
                            variant="outline-secondary"
                            onClick={handleRefresh}
                        >
                            REFRESH
                        </Button>
                        <DropdownAction items={getActionItems()} />
                        <Button
                            className="btn-min-width-120 btn-height-35"
                            variant="info"
                            disabled
                            onClick={() => navigate('/admin/match-types/create')}
                        >
                            CREATE
                        </Button>
                    </div>
                }
                searchBar={
                    <FormTextInput
                        placeholder={'Search by company id'}
                        value={searchCriteria}
                        onKeyDown={handleSearchOnKeyDown}
                        onChange={(value) => {
                            setSearchCriteria(value);
                        }}
                        clearable
                        clearableHandle={() => {
                            setSearchCriteria('');
                            setLastReload(Date.now());
                        }}
                    >
                    </FormTextInput>
                }
            />
            <div
                className="ag-theme-quartz"
                style={{ height: '680px' }}
            >
                {entityVerificationsState.entityVerificationsArray.length > 0 ?
                    <AgGridReact
                        rowData={entityVerificationsState.entityVerificationsArray}
                        columnDefs={colDefs}
                        pagination={false}
                        groupDisplayType='groupRows'
                        enableCellTextSelection="true"
                        ensureDomOrder="true"
                        onGridReady={onGridReady}
                        getRowId={params => params.data.id}
                        rowHeight={48}
                        suppressRowTransform={true}
                        getRowStyle={getRowStyle}
                        stopEditingWhenCellsLoseFocus={false}
                        suppressClickEdit={false}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        suppressAnimationFrame={true}
                        suppressColumnVirtualisation={false}
                        suppressRowVirtualisation={false}
                        suppressKeyboardEvent={params => {
                            console.log('Keyboard event', params);
                            // Only suppress events that might close the editor
                            return params.event.key === 'Escape' || params.event.key === 'Enter';
                        }}
                    /> :
                    `Loading Table...`
                }
            </div>
            <TableFooter
                countOfResults={entityVerificationsState.totalCount}
                resultsPerPage={pageSize}
                onSelectPage={handlePageSubmit}
                currentPage={pageIndex + 1} // Convert 0-based index to 1-based for UI
            />
        </Container>
    );
};

export default EntityVerifications;
